import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Fade,
  LinearProgress,
  Button
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon,
  Image as ImageIcon,
  Science as TestIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const DropZone = ({ onFileAccepted, isProcessing }) => {
  const [preview, setPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview({
          url: e.target.result,
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2)
        });
      };
      reader.readAsDataURL(file);
      
      // Simulate upload progress
      setUploadProgress(0);
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 10;
        });
      }, 100);
      
      // Pass file to parent
      setTimeout(() => {
        onFileAccepted(file);
      }, 1000);
    }
  }, [onFileAccepted]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: isProcessing
  });

  const clearPreview = () => {
    setPreview(null);
    setUploadProgress(0);
  };

  const handleUseSampleData = async () => {
    try {
      const response = await fetch("/sample.jpg");
      if (response.ok) {
        const blob = await response.blob();
        const file = new File([blob], "sample.jpg", { type: "image/jpeg" });
        onDrop([file]);
      }
    } catch (error) {
      console.error("Failed to load sample image:", error);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      {!preview ? (
        <Box>
          {/* Sample Data Button - positioned above the drop zone */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<TestIcon />}
              onClick={handleUseSampleData}
              disabled={isProcessing}
              sx={{
                borderColor: "warning.main",
                color: "warning.main",
                "&:hover": {
                  borderColor: "warning.light",
                  bgcolor: "rgba(245, 158, 11, 0.1)",
                },
                "&:disabled": {
                  borderColor: "text.disabled",
                  color: "text.disabled",
                }
              }}
            >
              Use Sample Data
            </Button>
          </Box>
          
          {/* Drop Zone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              {...getRootProps()}
              sx={{
                p: 6,
                textAlign: 'center',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                background: isDragActive 
                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))'
                  : 'rgba(30, 41, 59, 0.6)',
                backdropFilter: 'blur(10px)',
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'rgba(148, 163, 184, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05), rgba(139, 92, 246, 0.05))'
                }
              }}
            >
              <input {...getInputProps()} />
              
              <motion.div
                animate={{ 
                  scale: isDragActive ? 1.1 : 1,
                  rotate: isDragActive ? 5 : 0
                }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <CloudUploadIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: isDragActive ? 'primary.main' : 'text.secondary',
                    mb: 2
                  }} 
                />
              </motion.div>
              
              <Typography variant="h5" gutterBottom>
                {isDragActive ? 'Drop your image here' : 'Drag & drop your image'}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                or click to browse
              </Typography>
              
              <Typography variant="caption" color="text.secondary">
                Supported formats: JPG, PNG, GIF, WebP (max 10MB)
              </Typography>
            </Paper>
          </motion.div>
        </Box>
      ) : (
        <Fade in={true}>
          <Paper
            sx={{
              p: 3,
              background: 'rgba(30, 41, 59, 0.8)',
              backdropFilter: 'blur(10px)',
              position: 'relative'
            }}
          >
            {!isProcessing && (
              <IconButton
                onClick={clearPreview}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                  }
                }}
              >
                <ClearIcon />
              </IconButton>
            )}
            
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box
                component="img"
                src={preview.url}
                alt={preview.name}
                sx={{
                  width: 150,
                  height: 150,
                  objectFit: 'cover',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}
              />
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  <ImageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {preview.name}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Size: {preview.size} MB
                </Typography>
                
                {uploadProgress < 100 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Uploading... {uploadProgress}%
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                )}
                
                {uploadProgress === 100 && !isProcessing && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                    ✓ Ready to process
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default DropZone;